import { useState, useMemo, useId, useEffect } from "react";
import { Check, Plus, Search, X } from "lucide-react";
import { SideSheet } from "@/components/ui";
import { TagBadge } from "@/components/ui/Tag/TagBadge";
import { TagColorPicker } from "@/components/ui/Tag/TagColorPicker";
import { useDocumentTags, useCreateDocumentTag } from "@/hooks/use-documents";
import { useTranslation } from "@/packages/utils";
import type { DocumentViewTag } from "@/packages/types";
import { DEFAULT_TAG_COLOR } from "@/components/ui/Tag/types";

interface TagSelectorSheetProps {
  open: boolean;
  onClose: () => void;
  documentTags?: DocumentViewTag[];
  documentName?: string;
  onSave: (tagIds: string[]) => Promise<void>;
}

export function TagSelectorSheet({
  open,
  onClose,
  documentTags,
  documentName,
  onSave,
}: TagSelectorSheetProps) {
  const { t } = useTranslation(["documents", "common"]);
  const { tags: allTags, isLoading } = useDocumentTags();
  const createTag = useCreateDocumentTag();
  const formId = useId();
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Sync selected tags when sheet opens or document changes
  useEffect(() => {
    if (open) {
      setSelectedTagIds(documentTags?.map((t) => t.id) ?? []);
      setSearchQuery("");
      setIsCreating(false);
    }
  }, [open, documentTags]);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(DEFAULT_TAG_COLOR);

  const availableTags = useMemo(() => {
    return allTags.map((tag) => ({
      id: tag.id,
      name: tag.name,
      color: tag.color,
    }));
  }, [allTags]);

  const selectedTags = useMemo(() => {
    return availableTags.filter((tag) => selectedTagIds.includes(tag.id));
  }, [availableTags, selectedTagIds]);

  const filteredAvailableTags = useMemo(() => {
    if (!searchQuery)
      return availableTags.filter((tag) => !selectedTagIds.includes(tag.id));
    return availableTags.filter(
      (tag) =>
        !selectedTagIds.includes(tag.id) &&
        tag.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [availableTags, selectedTagIds, searchQuery]);

  const exactMatch = useMemo(() => {
    return availableTags.find(
      (tag) => tag.name.toLowerCase() === searchQuery.toLowerCase(),
    );
  }, [availableTags, searchQuery]);

  const handleToggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId],
    );
  };

  const handleRemoveTag = (tagId: string) => {
    setSelectedTagIds((prev) => prev.filter((id) => id !== tagId));
  };

  const handleStartCreate = () => {
    setIsCreating(true);
    setNewTagName(searchQuery);
    setNewTagColor(DEFAULT_TAG_COLOR);
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    try {
      const newTagId = await createTag({
        name: newTagName.trim(),
        color: newTagColor,
      });
      setSelectedTagIds((prev) => [...prev, String(newTagId)]);
      setSearchQuery("");
      setNewTagName("");
      setNewTagColor(DEFAULT_TAG_COLOR);
      setIsCreating(false);
    } catch {
      // Error handled by hook
    }
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewTagName("");
    setNewTagColor(DEFAULT_TAG_COLOR);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(selectedTagIds);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SideSheet open={open} onClose={onClose}>
      <SideSheet.Header
        title={t("documents:tags.title")}
        description={
          documentName
            ? t("documents:tags.description", { name: documentName })
            : undefined
        }
      />
      <SideSheet.Body>
        {isLoading ? (
          <div className="p-4 text-sm text-muted">
            {t("documents:tags.loading")}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Selected tags section */}
            <div>
              <p className="text-[11px] uppercase text-muted font-medium tracking-wider mb-2.5">
                {t("documents:tags.selected")}
              </p>
              {selectedTags.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {selectedTags.map((tag) => (
                    <TagBadge
                      key={tag.id}
                      name={tag.name}
                      color={tag.color}
                      onRemove={() => handleRemoveTag(tag.id)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted">
                  {t("documents:tags.noSelected")}
                </p>
              )}
            </div>

            <div className="border-t border-border" />

            {/* Search input */}
            <div>
              <p className="text-[11px] uppercase text-muted font-medium tracking-wider mb-2.5">
                {t("documents:tags.available")}
              </p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("documents:tags.searchPlaceholder")}
                  className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-card text-sm text-primary placeholder:text-muted outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-colors"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-secondary"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Available tags list */}
            <div className="space-y-1">
              {filteredAvailableTags.map((tag) => {
                const isSelected = selectedTagIds.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleToggleTag(tag.id)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-card-hover transition-colors cursor-pointer text-left"
                  >
                    <span
                      className="w-3 h-3 rounded-full shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="flex-1 text-sm text-secondary">
                      {tag.name}
                    </span>
                    <span
                      className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                        isSelected
                          ? "bg-accent border-accent text-white"
                          : "border-border"
                      }`}
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                    </span>
                  </button>
                );
              })}
              {filteredAvailableTags.length === 0 && !isCreating && (
                <div className="py-4 text-center text-sm text-muted">
                  {t("documents:tags.noFound")}
                </div>
              )}
            </div>

            {/* Create new tag section */}
            {(searchQuery && !exactMatch) || isCreating ? (
              <div className="border-t border-border pt-4">
                {isCreating ? (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-primary">
                      {t("documents:tags.createNew")}
                    </p>
                    <div>
                      <label
                        htmlFor={`${formId}-name`}
                        className="text-[13px] font-medium text-primary"
                      >
                        {t("common:labels.name")}
                      </label>
                      <input
                        id={`${formId}-name`}
                        type="text"
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        className="w-full h-10 mt-1 px-3 rounded-lg border border-border bg-card text-sm text-primary outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent"
                        placeholder={t("documents:tags.tagNamePlaceholder")}
                      />
                    </div>
                    <TagColorPicker
                      value={newTagColor}
                      onChange={setNewTagColor}
                      label={t("common:labels.color")}
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleCancelCreate}
                        className="flex-1 px-4 py-2 text-sm text-secondary hover:text-primary border border-border rounded-lg hover:bg-card-hover transition-colors"
                      >
                        {t("common:actions.cancel")}
                      </button>
                      <button
                        type="button"
                        onClick={handleCreateTag}
                        disabled={!newTagName.trim()}
                        className="flex-1 px-4 py-2 text-sm bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50"
                      >
                        {t("common:actions.create")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleStartCreate}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-accent hover:bg-card-hover rounded-lg transition-colors cursor-pointer"
                  >
                    <Plus className="h-4 w-4" />
                    {t("documents:tags.create", { name: searchQuery })}
                  </button>
                )}
              </div>
            ) : null}
          </div>
        )}
      </SideSheet.Body>
      <SideSheet.Footer>
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-secondary hover:text-primary transition-colors"
          >
            {t("common:actions.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50"
          >
            {isSaving ? t("documents:tags.saving") : t("common:actions.save")}
          </button>
        </div>
      </SideSheet.Footer>
    </SideSheet>
  );
}
